import { fetchFile } from '@ffmpeg/util';
import {
    formatFileSize,
    isSupportedVideoFile,
    loadFFmpeg,
    preloadFFmpeg,
    resetFFmpeg,
} from '../compress/compressVideoEngine';

export {
    formatFileSize,
    isSupportedVideoFile,
    preloadFFmpeg,
};

export const MAX_CONVERT_VIDEO_BYTES = 200 * 1024 * 1024;

export const AUDIO_FORMATS = ['mp3', 'aac', 'wav'];

const MIME_TYPES = {
    mp3: 'audio/mpeg',
    aac: 'audio/aac',
    wav: 'audio/wav',
};

export function convertedAudioName(fileName, format = 'mp3') {
    const base = (fileName || 'audio').replace(/\.[^.]+$/, '');
    const ext = AUDIO_FORMATS.includes(format) ? format : 'mp3';
    return `${base}.${ext}`;
}

function inputNameFor(file) {
    return `input${file.name.substring(file.name.lastIndexOf('.'))}`;
}

async function cleanupFiles(ffmpeg, ...names) {
    await Promise.all(names.map(async (name) => {
        try { await ffmpeg.deleteFile(name); } catch (_) { /* ignore */ }
    }));
}

/**
 * Same implementation as sohozkaj-site videoProcessor.videoToAudio.
 * Always writeFile — never WORKERFS (large files fail silently on single-thread core).
 */
export async function videoToAudio(file, format = 'mp3', onProgress = () => {}) {
    let ffmpeg;
    const safeFormat = AUDIO_FORMATS.includes(format) ? format : 'mp3';
    const inputName = inputNameFor(file);
    const outputName = `output.${safeFormat}`;

    try {
        onProgress(1);
        ffmpeg = await loadFFmpeg();
        if (!ffmpeg?.loaded) {
            throw new Error('FFMPEG_NOT_LOADED');
        }

        await cleanupFiles(ffmpeg, inputName, outputName);
        onProgress(5);
        await ffmpeg.writeFile(inputName, await fetchFile(file));
        onProgress(15);

        const progressHandler = ({ progress }) => {
            const pct = Math.round(Math.min(progress * 100, 100));
            onProgress(Math.min(99, 15 + Math.round(pct * 0.84)));
        };
        ffmpeg.on('progress', progressHandler);

        const args = ['-i', inputName, '-vn'];

        if (safeFormat === 'mp3') {
            args.push('-acodec', 'libmp3lame', '-b:a', '192k');
        } else if (safeFormat === 'aac') {
            args.push('-acodec', 'aac', '-b:a', '192k');
        } else if (safeFormat === 'wav') {
            args.push('-acodec', 'pcm_s16le');
        }

        args.push(outputName);

        const execTimeout = Math.max(180000, Math.ceil((file.size / (1024 * 1024)) * 15000));
        const exitCode = await ffmpeg.exec(args, execTimeout);

        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            throw new Error(exitCode === 1 ? 'TIMEOUT' : 'ENCODE_FAILED');
        }

        onProgress(98);
        const data = await ffmpeg.readFile(outputName);

        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        onProgress(100);
        return new Blob([data.buffer], { type: MIME_TYPES[safeFormat] || 'audio/mpeg' });
    } catch (error) {
        if (error?.message === 'FFMPEG_NOT_LOADED') {
            resetFFmpeg();
        }
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (
            error?.message === 'ENCODE_FAILED'
            || error?.message === 'EMPTY_OUTPUT'
            || error?.message === 'TIMEOUT'
            || error?.message === 'FFMPEG_NOT_LOADED'
        ) {
            throw error;
        }
        throw new Error('EXTRACT_FAILED');
    } finally {
        if (ffmpeg) {
            await cleanupFiles(ffmpeg, inputName, outputName);
        }
    }
}
